import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Message } from '@/state/types/message';
import { Notification } from './types/notification';
import { EventScope, ProjectEventType } from '@bimdata/definitions/const';
import { Location } from 'react-router-dom';
import { FeatureParams } from '@bimdata/definitions/types';
import t from '@/utils/translate';

export type CommonState = {
  messages: Message[];
  initiated: boolean;
  applicationBlocked: boolean;
  applicationUpgradeRequired: boolean;
  newNotifications: Notification[];
  deliveredNotifications: Notification[];
  exitPoint?: Location;
  features: Record<string, FeatureParams>;
};

const commons = createSlice({
  name: 'common',
  initialState: {
    messages: [],
    title: null,
    initiated: false,
    applicationBlocked: false,
    applicationUpgradeRequired: false,
    newNotifications: [],
    deliveredNotifications: [],
    features: {},
  } as CommonState,
  reducers: {
    reset: (state: CommonState) => {
      state.messages = [];
      state.newNotifications = [];
      state.deliveredNotifications = [];
      delete state.exitPoint;
    },
    initiate: (state: CommonState) => {
      state.initiated = false;
    },
    initiated: (state: CommonState) => {
      state.initiated = true;
    },
    showMessage: (state: CommonState, { payload }: PayloadAction<Message>) => {
      state.messages.push({
        type: payload.type,
        text: payload.text || t('error.unknown')
      });
      state.messages = state.messages.slice();
    },
    hideMessage: (state: CommonState, { payload }: PayloadAction<number>) => {
      state.messages = state.messages.filter((m, i) => i != payload);
    },
    blockApplication: (state: CommonState, { payload }: PayloadAction<boolean>) => {
      state.applicationBlocked = true;
      state.applicationUpgradeRequired = payload;
    },
    hideApplicationBlocked: (state: CommonState) => {
      state.applicationBlocked = false;
    },
    addNotifications: (state:CommonState, { payload }: PayloadAction<Array<Notification>>) => {
      const delivered = [];
      const notDelivered = [];
      for (const n of payload) {
        if (n.delivered) {
          delivered.push(n);
        } else {
          notDelivered.push(n);
        }
      }
      if (delivered.length) {
        state.deliveredNotifications = [
          ...state.deliveredNotifications,
          ...delivered,
        ].sort((a, b) => b.date.getTime() - a.date.getTime())
          .slice(0, 20);
      }
      if (notDelivered.length) {
        state.newNotifications = [
          ...state.newNotifications,
          ...notDelivered,
        ].sort((a, b) => b.date.getTime() - a.date.getTime());
      }
    },
    markNotificationsAsDelivered: (state: CommonState) => {
      state.deliveredNotifications = [
        ...state.deliveredNotifications,
        ...state.newNotifications.map((n) => ({
          ...n,
          delivered: true,
        })),
      ].sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 20);
      state.newNotifications = [];
    },
    removeLastProjectInviteNotification: (state: CommonState, { payload }: PayloadAction<string>) => {
      const notDeliveredIndex = state.newNotifications.findIndex((n) => (
        n.event.scope === EventScope.PROJECT
        && (
          n.event.data.project?.code === payload
          || n.event.data.projectCode === payload
        )
      ));
      if (notDeliveredIndex >= 0 && state.newNotifications[notDeliveredIndex].event.type === ProjectEventType.INVITE) {
        state.newNotifications = state.newNotifications.filter((v, i) => i !== notDeliveredIndex);
      }

      const deliveredIndex = state.deliveredNotifications.findIndex((n) => (
        n.event.scope === EventScope.PROJECT
        && (
          n.event.data.project?.code === payload
          || n.event.data.projectCode === payload
        )
      ));
      if (deliveredIndex >= 0 && state.deliveredNotifications[deliveredIndex].event.type === ProjectEventType.INVITE) {
        state.deliveredNotifications = state.deliveredNotifications.filter((v, i) => i !== deliveredIndex);
      }
    },
    setExitPoint: (state: CommonState, { payload }: PayloadAction<Location>) => {
      state.exitPoint = payload;
    },
    clearExitPoint: (state: CommonState, { }: PayloadAction<void>) => {
      delete state.exitPoint;
    },
    setFeatures: (state: CommonState, { payload }: PayloadAction<Record<string, FeatureParams>>) => {
      state.features = payload;
    }
  }
});

export default commons;
